import React from 'react';
import { useTranslation } from 'react-i18next';
import { useStoreActions, useStoreState } from '../../../../../models/hooks';
import { MultiSelect } from './MultiSelect';
import { ProvinceCities } from '../types';

function ProvinceSelectComponent() {
  const { t } = useTranslation('moniterByRecruiters');

  const { positionFilters } = useStoreState((state) => state.moniterByRecruiters);
  const { fetchBusinessAddress, setPositionFilter } = useStoreActions((state) => state.moniterByRecruiters);
  const [isLoading, setIsLoading] = React.useState(false);
  const [provinceCities, setProvinceCities] = React.useState<ProvinceCities | null>(null);
  const [provinces, setProvinces] = React.useState<string[]>([]);

  React.useEffect(() => {
    setIsLoading(true);
    fetchBusinessAddress()
      .then((res) => {
        setProvinceCities(res?.result?.provinceCities);
        setProvinces(Object?.keys(res?.result?.provinceCities ?? {}));
      })
      .finally(() => {
        setIsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const provinceOptions = React.useMemo(() => {
    if (!provinces || !positionFilters.businessIds?.length || !provinceCities) return [];

    return provinces
      .filter((province) => {
        const c = provinceCities[province];
        /* istanbul ignore next */ if (!c) return false;

        // Flatten all locationIds under this province
        const locationIds = Object.values(c).flat();

        // Keep province only if it intersects with filter locations
        return locationIds.some((id) => positionFilters?.businessIds?.includes(id));
      })
      .map((p) => ({ value: p, label: p }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provinces, provinceCities, positionFilters.businessIds]);

  React.useEffect(() => {
    setPositionFilter({ key: 'province', value: provinceOptions.map((opt) => opt.value) });
    setPositionFilter({ key: 'isAllProvinceSelected', value: true });
  }, [provinceOptions, setPositionFilter]);

  const cityOptions = React.useMemo(() => {
    if (!positionFilters.province?.length || !positionFilters.businessIds?.length || !provinceCities) {
      return [];
    }

    const provincesToCheck = positionFilters.province;

    return provincesToCheck.flatMap((province) => {
      const c = provinceCities[province];
      /* istanbul ignore next */ if (!c) return [];

      return Object.keys(c)
        .filter((city) => {
          const locationIds = c[city];
          return locationIds.some((id) => positionFilters?.businessIds?.includes(id));
        })
        .map((city) => ({ value: city, label: city }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positionFilters.province, positionFilters.businessIds, provinceCities]);

  React.useEffect(() => {
    setPositionFilter({ key: 'city', value: cityOptions.map((opt) => opt.value) });
    setPositionFilter({ key: 'isAllCitySelected', value: true });
  }, [cityOptions, setPositionFilter]);

  return (
    <>
      <MultiSelect
        options={provinceOptions}
        selectedValues={positionFilters.province ?? /* istanbul ignore next */ []}
        onChange={(vals, selectAll) => {
          setPositionFilter({ key: 'province', value: vals });
          setPositionFilter({ key: 'isAllProvinceSelected', value: selectAll });
        }}
        selectAll={positionFilters.isAllProvinceSelected}
        label={t('filter.province.label')}
        allSelectText={t('filter.province.all')}
        placeholder={t('filter.province.search')}
        isLoading={isLoading}
        height={400}
        itemHeight={30}
      />
      <MultiSelect
        options={cityOptions}
        selectedValues={positionFilters.city ?? /* istanbul ignore next */ []}
        onChange={(vals, selectAll) => {
          setPositionFilter({ key: 'city', value: vals });
          setPositionFilter({ key: 'isAllCitySelected', value: selectAll });
        }}
        selectAll={positionFilters.isAllCitySelected}
        label={t('filter.city.label')}
        allSelectText={t('filter.city.all')}
        placeholder={t('filter.city.search')}
        isLoading={isLoading}
        height={400}
        itemHeight={30}
      />
    </>
  );
}

export const ProvinceSelect = React.memo(ProvinceSelectComponent);
